import { ProfileSkillsListService } from '@app/profile/services/profile-skills-list.service';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { isKey, Key } from '@app/shared/key';
import { AuthService } from '@app/shared/services/auth.service';
import {
  getAverageRatingLevel,
  tagHasIncompleteRatingsForType,
  tagHasRedactedRatings,
} from '@app/shared/utils/tag-helpers';
import { TagModalService } from '@app/tags/services/tag-modal.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-profile-skills-list-peer-ratings',
  templateUrl: './profile-skills-list-peer-ratings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSkillsListPeerRatingsComponent implements OnInit {
  @Input() public tag: TagsApi.TagDetails;
  @Input() public canRequestPeerRating: boolean;
  @Input() public isOwner: boolean;
  @Input() public showLock: boolean;

  public i18n: { [key: string]: string } = {};
  public dgat: string;
  public tabIndex: 0 | undefined;
  public role: 'button' | undefined;

  constructor(
    private tagsService: TagsService,
    private translateService: TranslateService,
    private tagModalService: TagModalService,
    private authService: AuthService,
    private profileSkillsListService: ProfileSkillsListService
  ) {}

  /** Get average of peer ratings */
  public get level(): string | undefined {
    return getAverageRatingLevel(this.completedRatings);
  }

  public get showDonut(): boolean {
    return (
      (this.isOwner && this.canRequestPeerRating) ||
      !!this.level ||
      (this.isOwner && this.inProgress)
    );
  }

  public get showDefaultPlus(): boolean {
    return !this.level && this.isOwner && !this.inProgress;
  }

  public get ariaLabel(): string {
    let label = '';
    if (this.level) {
      label = this.translateService.instant(
        'dgTagRating_A11yRequestUpdatePeerRating',
        {
          skillName: this.tag.title,
          levelNumber: this.level,
        }
      );
    } else {
      label = this.translateService.instant(
        'dgTagRating_A11yRequestPeerRating',
        {
          skillName: this.tag.title,
        }
      );
    }
    label = this.profileSkillsListService.getAriaLabelText(
      label,
      this.privacyText,
      ''
    );
    return label;
  }

  public get tooltip(): string {
    return this.profileSkillsListService.getTooltipText(
      this.baseText,
      this.privacyText
    );
  }

  public get inProgress(): boolean {
    return tagHasIncompleteRatingsForType(
      this.tag,
      InternalTagRatingTypes.peer
    );
  }

  private get levelText(): string {
    return this.profileSkillsListService.getLevelText(this.level);
  }

  private get baseText() {
    return this.level
      ? this.i18n.dgTagRating_PeerRatings
      : this.i18n.dgTagRating_RequestPeerRating;
  }

  private get privacyText() {
    return this.profileSkillsListService.getPrivacyText(
      this.level,
      this.isOwner,
      this.showLock,
      this.authService.authUser?.participateInTeamProgram ?? false
    );
  }

  private get completedRatings(): TagsApi.UserTagRating[] {
    return this.tag?.ratings?.filter(
      (rating) => rating.type === InternalTagRatingTypes.peer && rating.level
    );
  }

  public ngOnInit(): void {
    this.i18n = this.translateService.instant(
      [
        'dgTagRating_PeerRatings',
        'dgTagRating_RatingTitleFormat',
        'dgTagRating_RequestPeerRating',
        'Core_Private',
        'Core_LimitedVisibility',
      ],
      {
        tagName: this.tag.title,
      }
    );
    this.dgat = this.isOwner
      ? `profile-skills-list-peer-cta-${this.tag.name}`
      : undefined;
    this.tabIndex = this.isOwner ? 0 : undefined;
    this.role = this.isOwner ? 'button' : undefined;
  }

  public clickHandler(event: MouseEvent): void {
    this.eventHandler(event);
  }

  public keydownHandler(event: KeyboardEvent): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      // prevent spacebar from scrolling screen
      event.preventDefault();
      this.eventHandler(event);
    }
  }

  private eventHandler(event: Event): void {
    if (this.authService.authUser) {
      // prevent event from triggering skill modal
      event.stopPropagation();
      const peerRatings = this.tag.ratings.filter(
        (r) => r.type === InternalTagRatingTypes.peer
      );
      const hasRedactedRating = tagHasRedactedRatings(peerRatings);

      if (peerRatings.length > 0 && !hasRedactedRating) {
        this.openPeerRatingOverviewModal();
      } else {
        if (this.isOwner) {
          this.tagModalService.openPeerRatingRequestModal(event, this.tag);
        }
      }
    }
  }

  /** Show all pending/completed peer ratings */
  private openPeerRatingOverviewModal(): void {
    this.tagsService
      .getTagRatingDetails(this.tag.ratings[0].userProfileKey, this.tag.tagId)
      .subscribe((ratings) => {
        const pendingRatings = ratings.filter(
          (rating) =>
            rating.type === InternalTagRatingTypes.peer && !rating.level
        );
        const completedRatings = ratings.filter(
          (rating) =>
            rating.type === InternalTagRatingTypes.peer && rating.level
        );
        this.tagModalService.openPeerRatingOverviewModal(
          this.tag,
          pendingRatings,
          completedRatings,
          this.isOwner
        );
      });
  }

  public getLevelDescription(level: string): string {
    return this.tagsService.orgLevels.find((l) => l.value.toString() === level)
      ?.description;
  }
}
